<template>
  <div class="d-flex flex-column min-vh-100">
    <div class="layout-center d-flex justify-content-center align-items-start flex-wrap gap-3 p-3 flex-grow-1">
    <main v-if="exposedOrder" class="form-main overflow-hidden mel-glass-panel">
      <Form id="login" @submit="onSubmit" :validation-schema="schema" v-slot="{ errors, isSubmitting }" class="m-2">
        <div class="d-flex justify-content-between">
          <div class="d-flex flex-column justify-content-start gap-1">
            <!-- <div class="logo px-3">
              <img alt="Logo" src="/logo.svg" class="logo"/>
            </div> -->
            <span class="h3">{{ $t('menu.order-confirmation') }}</span>
          </div>
          <div class="d-flex flex-column justify-content-start gap-1">
            <span v-if="isLoading" class="h3 fw-bold text-primary">
              <span>{{ $t('status.loadingEllipsis') }}</span>
            </span>
            <span v-else class="h3 fw-bold text-primary">{{ exposedOrder?.code }}</span>
            <div v-if="showDeliveryDate" class="form-floating">
              <Field id="deliverydate" name="deliverydate" type="date" class="form-control"
                :class="{ 'is-invalid': errors.deliverydate }" v-model="deliverydate" required v-focus :min="minDate"
                :max="maxDate" :disabled="isSubmitting || exposedOrder?.status !== ORDER_HEAD_STATUS.pending" />
              <label for="deliverydate">{{ $t('field.order.deliveryDate') }}</label>
            </div>
          </div>
        </div>
        <button v-if="exposedOrder.status === ORDER_HEAD_STATUS.pending" class="btn btn-primary w-100 mt-3 py-2" type="submit" :disabled="isSubmitting || isLoading">
          <span v-show="isSubmitting" class="spinner-border spinner-border-sm mr-1"></span>
          <span>{{ $t('action.acceptOrder') }}</span>
        </button>
        <div v-else class="alert alert-success mt-3 mb-0">
          <font-awesome-icon :icon="['fas', 'check']" />
          {{ $t('statement.orderIsAccepted') }}
        </div>
        <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>
      </Form>
      <div class="mel-glass-panel mel-glass-panel-inset p-2">
        <div v-if="isLoading" class="text-center text-primary">
          <span class="spinner-border spinner-border-lg"></span>
        </div>
        <div v-else class="table-fix-head">
          <table class="table table-sm table-responsive align-middle mb-0">
            <thead>
              <tr>
                <th>{{ $t('field.generic.quantity') }}</th>
                <th>{{ $t('field.generic.code') }}</th>
                <th>{{ $t('field.generic.item') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in exposedOrder.lines" :key="index">
                <td><QuantityField :quantity="item.quantity" :unit-name="item.unitName" :pad-length="maxQuantityLength" /></td>
                <td>
                  <span class="text-secondary">{{ item.exposedSupplierItem?.code ?? item.exposedItem.code }}</span>
                </td>
                <td>
                  <span class="text-primary fw-bold">{{ item.exposedItem.name }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>
    <div v-if="exposedOrder" class="panel-right ms-3">
      <div class="card mel-glass-panel">
        <h4 class="card-header">{{ $t('field.generic.supplier') }}</h4>
        <div class="card-body">
          <div class="text-primary fw-bold">{{ exposedOrder.exposedSupplier.companyName }}</div>
          <div v-if="exposedOrder.exposedSupplier.address" style="white-space: pre-line; padding-left: 24px; text-indent: -24px;"><font-awesome-icon :icon="['fas', 'map-location-dot']" /> {{ exposedOrder.exposedSupplier.address }}</div>
          <div v-if="exposedOrder.exposedSupplier.phone"><font-awesome-icon :icon="['fas', 'phone']" /> {{ exposedOrder.exposedSupplier.phone }}</div>
          <div v-if="exposedOrder.exposedSupplier.contactName"><font-awesome-icon :icon="['fas', 'user']" /> {{ exposedOrder.exposedSupplier.contactName }}</div>
        </div>
      </div>
      <div class="text-center mel-text-glass mt-3"><font-awesome-icon :icon="['fas', 'chevron-down']" size="2x" /></div>
      <div class="card mel-glass-panel mt-3">
        <h4 class="card-header d-flex justify-content-between align-items-center gap-2">
          <span>{{ $t('field.generic.customer') }}</span>
          <small class="text-secondary">{{ exposedOrder.exposedSupplier.customerCode }}</small>
        </h4>
        <div class="card-body">
          <div class="text-primary fw-bold">{{ exposedOrder.exposedSite.companyName }}</div>
          <div v-if="exposedOrder.exposedSite.address" style="white-space: pre-line; padding-left: 24px; text-indent: -24px;"><font-awesome-icon :icon="['fas', 'map-location-dot']" /> {{ exposedOrder.exposedSite.address }}</div>
          <div v-if="exposedOrder.exposedSite.phone"><font-awesome-icon :icon="['fas', 'phone']" /> {{ exposedOrder.exposedSite.phone }}</div>
          <div v-if="exposedOrder.exposedSite.contactName"><font-awesome-icon :icon="['fas', 'user']" /> {{ exposedOrder.exposedSite.contactName }}</div>
        </div>
      </div>
    </div>
    </div>
    <FooterComponent />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from 'vue-i18n';
import { storeToRefs } from "pinia";
import { Id, toast, type ToastOptions } from 'vue3-toastify';
import { Form, Field, GenericObject } from 'vee-validate';
import * as Yup from 'yup';
import { useExposedOrderStore } from "@/stores";
import FooterComponent from "@/components/FooterComponent.vue";
import { QuantityField } from '@/components/fields';
import { ORDER_HEAD_STATUS } from "@/models/Enums";

const props = defineProps({
  orderId: { type: Number, required: true }
});

const route = useRoute();
const i18n = useI18n();
const exposedOrderStore = useExposedOrderStore();

const routeOrderId = props.orderId;

const hashSearchParams = new URLSearchParams(route.hash.replace(/^#/, ""));
const hashParams = Object.fromEntries(hashSearchParams.entries());
const routeToken = hashParams.token as string;

const { order: exposedOrder } = storeToRefs(exposedOrderStore);

const isLoading = ref(true);
const isSaving = ref(false);
const toastId = ref('' as Id);
const deliverydate = ref(new Date().toISOString().split('T')[0] as string);
let now = new Date();
const minDate = now.toISOString().split('T')[0];
const maxDate = new Date(now.setDate(now.getDate() + 365)).toISOString().split('T')[0];

const schema = Yup.object().shape({
  deliverydate: Yup.string().required(i18n.t('validation.required'))
});

const maxQuantityLength = computed(() => {
  return exposedOrder.value?.lines.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

const showDeliveryDate = computed(() => exposedOrder.value?.exposedSupplier?.autoConfirmOrder === false);

onMounted(async () => {
  await exposedOrderStore.fetch(routeOrderId, routeToken);
  if (exposedOrder.value?.deliveryDate) {
    deliverydate.value = new Date(exposedOrder.value.deliveryDate).toISOString().split('T')[0] as string;
  }
  isLoading.value = false;

  if (exposedOrder.value?.status === ORDER_HEAD_STATUS.pending && !showDeliveryDate.value) {
    await acceptOrder(deliverydate.value);
  }
});

async function acceptOrder(deliveryDate: string) {
  isSaving.value = true;
  toastId.value = toast.loading(i18n.t('status.savingEllipsis'), {
    autoClose: false,
    theme: 'colored'
  } as ToastOptions);
  await exposedOrderStore.accept(routeOrderId, deliveryDate as unknown as Date, routeToken, onApiFulfilled, onApiRejected);
}

async function onSubmit(values: GenericObject, { setErrors }: GenericObject) {
  const { deliverydate } = values;

  try {
    await acceptOrder(deliverydate);
  } catch (error) {
    return setErrors({ apiError: error });
  }
}

const onApiFulfilled = () => {
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('statement.orderIsAccepted'),
      type: toast.TYPE.SUCCESS,
      isLoading: false,
      autoClose: 10000,
      closeOnClick: true
    } as ToastOptions);
};

const onApiRejected = (error: Error) => {
    isSaving.value = false;
    toast.update(toastId.value, {
      render: i18n.t('error.apiError', { error: error.message }),
      closeOnClick: true,
      type: toast.TYPE.ERROR,
      isLoading: false
    } as ToastOptions);
};
</script>

<style scoped type="scss">
.mel-text-glass {
  color: rgba(255, 255, 255, 0.6);
  filter: blur(1px);
  -webkit-filter: blur(1px);
}

.form-main {
  transition: all 0.4s ease 0s;
  flex: 0 1 640px;
  min-width: 0;
  padding: 1rem;
}

@media (min-width: 768px) {
  .form-main {
    max-width: 640px;
  }
}

.form-main .form-floating:focus-within {
  z-index: 2;
}

.panel-left {
  transition: all 0.4s ease 0s;
  flex: 0 0 auto;
  min-width: 320px;
  max-width: 360px;
}

.panel-right {
  transition: all 0.4s ease 0s;
  flex: 0 0 auto;
  min-width: 320px;
  max-width: 360px;
}

.table th,
.table td {
  background-color: transparent !important;
}

.table tr:last-child td {
  border-bottom: 0;
}

input[type="date"] {
  max-width: 200px;
  width: 100%; /* Makes it flexible */
}
</style>
